import { createContext, useContext } from "react";
import type { AuthState } from "@/services/authService";

export interface PlayerSummaryData {
  playerName: string;
  jgsRank: string;
  classRank: string;
  classYear: string;
  profileGradYear: string;
  scoringDifferential: string;
  isLinked: boolean;
}

export interface ChatHistoryItem {
  id: string;
  title: string;
  timestamp: string;
  messageCount?: number;
}

export interface EndUserContextValue {
  // Auth
  authState: AuthState | null;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState | null>>;

  // Player summary (for header)
  playerSummary: PlayerSummaryData;
  updatePlayerSummary: (partial: Partial<PlayerSummaryData>) => void;

  // User type
  userType: "player" | "coach";
  setUserType: (t: "player" | "coach") => void;

  // Sidebar toggle (header hamburger <-> TwoPaneLayout)
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;

  // Chat history (for sidebars on non-Index pages)
  chatHistory: ChatHistoryItem[];
  setChatHistory: (items: ChatHistoryItem[]) => void;

  // Logout
  handleLogout: () => Promise<void>;

  // Header callbacks (set by pages)
  onShare?: () => void;
  setOnShare: (fn: (() => void) | undefined) => void;

  // Raw user data from layout's initial /users/me fetch
  currentUser: any | null;

  // Re-fetch /users/me and update summary (e.g. after linking JGS profile)
  refreshUserData: () => Promise<void>;

  // Subscription gating (Individual Signup users only)
  subscriptionState: string | null;
  subscriptionEndDate: string | null;
  setSubscriptionGate: (state: string) => void;

  // Dismissed next steps (server-persisted on the user profile)
  dismissedNextSteps: string[];
  dismissNextStep: (key: string) => Promise<void>;
  resetDismissedNextSteps: () => Promise<void>;
}

export const defaultSummary: PlayerSummaryData = {
  playerName: "Player Name",
  jgsRank: "N/A",
  classRank: "N/A",
  classYear: "N/A",
  profileGradYear: "",
  scoringDifferential: "N/A",
  isLinked: false,
};

export const EndUserContext = createContext<EndUserContextValue | null>(null);

export function useEndUser(): EndUserContextValue {
  const ctx = useContext(EndUserContext);
  if (!ctx) throw new Error("useEndUser must be used within EndUserLayout");
  return ctx;
}
